import { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import { useBasicAlerts } from "../../../../components/BasicAlert";
import { User } from "../../../../models/User";
import { ApplicationState } from "../../../../store";
import { useAddUserGroupMutation, useGetUsersQuery } from "../../../../store/api/kinesense";

export interface AddUserGroupModalProps {
    isVisible: boolean;
    setIsVisible: (nextIsVisible: boolean) => void;
}

export const FormField = {
    Name: "name",
    Description: "description",
    Users: "users",
} as const;
export type FormField = (typeof FormField)[keyof typeof FormField];
export type FormData = Record<FormField, string>;
export const EMPTY_FORM_VALUES = Object.values(FormField).reduce((acc, key) => {
    acc[key] = "";
    return acc;
}, {}) as Readonly<FormData>;
const FORM_VALIDATION: Record<FormField, object> = {
    [FormField.Name]: {
        minLength: 3,
        maxLength: 24,
        title: "Group name must be between 3-24 characters long",
    },
    [FormField.Description]: {
        maxLength: 512,
        title: "Description must be no longer than 512 characters",
    },
    [FormField.Users]: {
        maxLength: 254,
    },
} as const;

export function AddUserGroupModal(props: AddUserGroupModalProps) {
    const modalTitleId = "createUserGroupTitle";
    const user = useSelector((state: ApplicationState) => state.user);
    const userCanViewOtherUsers =
        User.can(user.permissions.permissions, "read", "users") ||
        User.can(user.permissions.permissions, "peek", "users");
    const { data: users } = useGetUsersQuery({}, { skip: !userCanViewOtherUsers });

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [allowedUsers, setAllowedUsers] = useState<User[]>([]);
    const [requestAddUserGroup] = useAddUserGroupMutation();
    const { alerts, addAlert, clearAlerts } = useBasicAlerts();

    const hideModal = () => {
        props.setIsVisible(false);
    };

    const handleAllowedUsersChange = (selectedOptions: { value: string; label: string }[]) => {
        if (users) {
            const selectedUsers = users.filter((user) =>
                selectedOptions.some((option) => option.value === user.username),
            );

            setAllowedUsers(selectedUsers);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        console.log("handleSubmit");
        e.preventDefault();

        requestAddUserGroup({
            name,
            description,
            usernames: allowedUsers.map((user) => user.username),
        })
            .unwrap()
            .then(() => {
                hideModal();
                resetForm();
            })
            .catch((error) => {
                console.error("Failed to create user group:", error);
            });
    };

    const resetForm = () => {
        setName("");
        setDescription("");
        setAllowedUsers([]);
    };

    return (
        <Modal
            id="newUserModal"
            show={props.isVisible}
            aria-labelledby={modalTitleId}
            onHide={hideModal}
            centered
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title id={modalTitleId}>New User Group</Modal.Title>
            </Modal.Header>

            <Form className="new-resource-form" onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="group" controlId={FormField.Name}>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="groupName"
                            value={name || ""}
                            onChange={(e) => setName(e.target.value)}
                            {...FORM_VALIDATION[FormField.Name]}
                            placeholder="Group Name"
                        />
                    </Form.Group>

                    <Form.Group className="group" controlId={FormField.Description}>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="groupDescription"
                            type="text"
                            maxLength={1024}
                            value={description || ""}
                            style={{ minHeight: "8em" }}
                            onChange={(e) => setDescription(e.target.value)}
                            {...FORM_VALIDATION[FormField.Description]}
                            placeholder="Group Description"
                        />
                    </Form.Group>
                    <Form.Group className="group" controlId={FormField.Users}>
                        <Form.Label>Users</Form.Label>
                        {users && (
                            <Select
                                value={allowedUsers.map((user) => ({
                                    value: user.username,
                                    label: user.username,
                                }))}
                                options={users.map((user) => ({
                                    value: user.username,
                                    label: user.username,
                                }))}
                                placeholder="Add users to group..."
                                classNamePrefix="react-select"
                                isMulti
                                onChange={handleAllowedUsersChange}
                            />
                        )}
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button type="submit" variant="success">
                        Create User Group
                    </Button>
                    <Button variant="secondary" onClick={hideModal}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
